import React, { useMemo, useEffect, useState } from 'react';
import { guardarClientesAsignados, obtenerClientesAsignados } from '../db/indexedDB';
import { API_BASE, formatApiError, getToken, isAuthError, notifySessionExpired } from '../services/api';

const ordenarPorMonto = (items) => (
  [...items].sort((a, b) => (
    Number(b.prioridad_admin || 0) - Number(a.prioridad_admin || 0) ||
    Number(b.deuda_vencida || 0) - Number(a.deuda_vencida || 0)
  ))
);

const clienteVisibleParaTecnico = (cliente) => !['finalizado', 'revision_admin', 'reprogramado'].includes(cliente.estado);
const tieneCoordenadas = (cliente) => (
  cliente.latitud !== null &&
  cliente.longitud !== null &&
  cliente.latitud !== undefined &&
  cliente.longitud !== undefined
);

const RutaTecnico = ({ onSelectCliente }) => {
  const [clientes, setClientes] = useState([]);
  const [clientesAsignados, setClientesAsignados] = useState([]);
  const [filtroComuna, setFiltroComuna] = useState('');
  const [ubicacionActual, setUbicacionActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const comunas = useMemo(() => (
    [...new Set(clientesAsignados.map((cliente) => cliente.comuna).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [clientesAsignados]);

  const resumenRuta = useMemo(() => ({
    total: clientes.length,
    conGps: clientes.filter(tieneCoordenadas).length,
    prioridad: clientes.filter((cliente) => Number(cliente.prioridad_admin || 0) > 0).length,
    deuda: clientes.reduce((total, cliente) => total + Number(cliente.deuda_vencida || 0), 0),
  }), [clientes]);

  useEffect(() => {
    fetch(`${API_BASE}/tecnicos/mis-clientes`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          if (isAuthError(res, data)) notifySessionExpired();
          throw new Error(formatApiError(res, data, 'No se pudieron cargar las comunas asignadas.'));
        }
        const clientesData = Array.isArray(data) ? data : [];
        setClientesAsignados(clientesData);
        setClientes(ordenarPorMonto(clientesData.filter(clienteVisibleParaTecnico)));
        await guardarClientesAsignados(clientesData);
      })
      .catch(async (err) => {
        const cachedClientes = await obtenerClientesAsignados().catch(() => []);
        const visibles = cachedClientes.filter(clienteVisibleParaTecnico);
        if (visibles.length) {
          setClientesAsignados(visibles);
          setClientes(ordenarPorMonto(visibles));
          setError('Sin señal: usando ruta local ordenada por deuda. GPS y navegación externa pueden depender del teléfono.');
          return;
        }
        setError(err.message);
      });

    if (!navigator.geolocation) {
      setError('El navegador no permite obtener GPS para calcular la ruta.');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUbicacionActual(coords);
        obtenerRutas(coords.lat, coords.lng, filtroComuna);
      },
      () => {
        setError('Habilita el GPS para ordenar tu ruta.');
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (!ubicacionActual) return;
    obtenerRutas(ubicacionActual.lat, ubicacionActual.lng, filtroComuna);
  }, [filtroComuna]);

  const obtenerRutas = async (lat, lng, comuna = '') => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        lat_actual: String(lat),
        lng_actual: String(lng),
      });
      if (comuna) params.set('comuna', comuna);

      const res = await fetch(`${API_BASE}/tecnicos/mis-rutas?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (isAuthError(res, data)) notifySessionExpired();
        throw new Error(formatApiError(res, data, 'No se pudo obtener la ruta.'));
      }
      setClientes(ordenarPorMonto(data.clientes_ordenados || []));
    } catch (err) {
      const cachedClientes = await obtenerClientesAsignados().catch(() => []);
      const visibles = cachedClientes
        .filter(clienteVisibleParaTecnico)
        .filter((cliente) => !comuna || cliente.comuna === comuna);
      const visiblesOrdenados = ordenarPorMonto(visibles);
      if (visiblesOrdenados.length) {
        setClientes(visiblesOrdenados);
        setError('Sin señal: usando ruta local ordenada por deuda.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const navegarGoogleMaps = (lat, lng) => {
    if (lat === null || lng === null || lat === undefined || lng === undefined) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, '_blank');
  };

  const navegarWaze = (lat, lng) => {
    if (lat === null || lng === null || lat === undefined || lng === undefined) return;
    window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank');
  };

  return (
    <div className="ruta-container">
      <div className="route-header">
        <div>
          <span className="map-view__eyebrow">Jornada en terreno</span>
          <h2>Mi ruta de visitas</h2>
          <p>Ordenada por prioridad admin y luego deuda vencida, de mayor a menor.</p>
        </div>
        <div className="gv-form-group">
          <label className="gv-label" htmlFor="route-commune-filter">Filtrar por comuna</label>
          <select id="route-commune-filter" className="gv-select" value={filtroComuna} onChange={(e) => setFiltroComuna(e.target.value)}>
            <option value="">Todas las comunas</option>
            {comunas.map((comuna) => (
              <option key={comuna} value={comuna}>{comuna}</option>
            ))}
          </select>
        </div>
      </div>
      {!loading && clientes.length > 0 && (
        <section className="route-summary" aria-label="Resumen de la ruta">
          <div><span>Paradas</span><strong>{resumenRuta.total}</strong><small>{filtroComuna || 'Todas las comunas'}</small></div>
          <div><span>Con navegación</span><strong>{resumenRuta.conGps}</strong><small>{resumenRuta.total - resumenRuta.conGps} sin coordenadas</small></div>
          <div><span>Prioridad admin</span><strong>{resumenRuta.prioridad}</strong><small>Atender primero</small></div>
          <div className="route-summary__debt"><span>Deuda de la ruta</span><strong>${resumenRuta.deuda.toLocaleString('es-CL')}</strong><small>Total visible</small></div>
        </section>
      )}
      {error && <div className="gv-alert gv-alert--warning" role="status">{error}</div>}
      {loading && <p className="import-empty" role="status">Calculando ruta óptima...</p>}
      <ul className="lista-clientes">
        {!loading && clientes.map((cliente, index) => (
          <li key={cliente.id} className={`tarjeta-cliente${Number(cliente.prioridad_admin || 0) > 0 ? ' tarjeta-cliente--priority' : ''}`}>
            <div className="route-stop" aria-hidden="true">{index + 1}</div>
            <div className="info">
              <span className="route-client-code">Gestión {cliente.num_gestion} · Cliente {cliente.cod_cliente}</span>
              <h3>{cliente.r_social || 'Cliente sin razón social'}</h3>
              <p>{cliente.direccion}{cliente.comuna ? `, ${cliente.comuna}` : ''}</p>
              <div className="route-card__signals">
                {Number(cliente.prioridad_admin || 0) > 0 && <span className="route-chip route-chip--priority">Prioridad admin</span>}
                <span className={`route-chip ${tieneCoordenadas(cliente) ? 'route-chip--ok' : 'route-chip--warning'}`}>
                  {tieneCoordenadas(cliente) ? 'Navegación disponible' : 'Sin coordenadas'}
                </span>
              </div>
              <p className="route-debt">Deuda vencida: ${Number(cliente.deuda_vencida || 0).toLocaleString('es-CL')}</p>
              {cliente.coordenada_aproximada && (
                <p className="route-warning">Coordenada aproximada por comuna. Confirmar dirección antes de navegar.</p>
              )}
              {cliente.distancia_rutae_m && (
                <p>Distancia: {(cliente.distancia_rutae_m / 1000).toFixed(1)} km</p>
              )}
            </div>
            <div className="btn-group-nav">
              <button type="button" className="gv-btn gv-btn--primary" onClick={() => onSelectCliente?.(cliente)}>
                Abrir ficha
              </button>
              <button type="button" className="gv-btn gv-btn--outline" disabled={!tieneCoordenadas(cliente)} onClick={() => navegarGoogleMaps(cliente.latitud, cliente.longitud)}>
                Google Maps
              </button>
              <button type="button" className="gv-btn gv-btn--outline" disabled={!tieneCoordenadas(cliente)} onClick={() => navegarWaze(cliente.latitud, cliente.longitud)}>
                Waze
              </button>
            </div>
          </li>
        ))}
      </ul>
      {!loading && clientes.length === 0 && <p className="import-empty">No hay clientes asignados para esa comuna.</p>}
    </div>
  );
};

export default RutaTecnico;
